import {
  Avatar,
  Button,
  Center,
  LoadingOverlay,
  ModalProps,
  Stack,
  Text,
} from "@mantine/core"
import { DropzoneProps, MIME_TYPES } from "@mantine/dropzone"
import { CancelButton, DeleteButton } from "../../../-components/Buttons"
import { FormButtonGroup } from "../../../-components/form-button-group"
import { DefaultModal } from "../../../-components/default-modal"
import { useFileDialog } from "@mantine/hooks"
import ImageCropper from "../../../-components/ImageCropper"
import { useTranslation } from "react-i18next"

interface Props extends Omit<ModalProps, "opened"> {
  opened?: boolean
  title?: string
  initialImageUrl?: string | null
  aspect?: number
  maxSizeBytes?: number
  onUpload: (file: File) => Promise<unknown> | void
  onDelete?: () => Promise<unknown> | void
}

export function PickAndCropImageModal({
  initialImageUrl = null,
  aspect = 1,
  maxSizeBytes = 30 * 1024 ** 2,
  onUpload,
  onDelete,
  ...props
}: Props & Partial<DropzoneProps>) {
  const { opened, onClose, ...rest } = props as Props & Partial<DropzoneProps>
  const [file, setFile] = React.useState<File | null>(null)
  const [imageSrc, setImageSrc] = React.useState<string | null>(null)
  const [cropperOpened, setCropperOpened] = React.useState(false)
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    return () => {
      if (imageSrc) URL.revokeObjectURL(imageSrc)
    }
  }, [imageSrc])

  const fileDialog = useFileDialog({
    onChange: (files) => {
      if (files && files.length > 0) {
        const f = files[0]
        if (f.type !== MIME_TYPES.png && f.type !== MIME_TYPES.jpeg) {
          alert("File type not supported")
          return
        }
        if (f.size > maxSizeBytes) {
          alert("File size too large")
          return
        }
        const src = URL.createObjectURL(f)
        setImageSrc(src)
        setCropperOpened(true)
      }
    },
    multiple: false,
  })

  async function handleConfirmUpload() {
    if (!file) return
    try {
      setLoading(true)
      await onUpload(file)
      setFile(null)
      setImageSrc(null)
      onClose && onClose()
    } finally {
      setLoading(false)
    }
  }
  const { t } = useTranslation("app_server")

  return (
    <DefaultModal
      {...rest}
      opened={!!opened}
      onClose={onClose}
      title={t("ChooseImage")}
    >
      <Stack>
        <Center>
          <Avatar
            size={256}
            src={
              file
                ? URL.createObjectURL(file)
                : (imageSrc ?? initialImageUrl ?? undefined)
            }
            onClick={fileDialog.open}
            style={{ cursor: "pointer" }}
          />
        </Center>
        <Text fz={"sm"} fw={"lighter"}>
          {t("PickAndCropImage")}
        </Text>

        <FormButtonGroup wrap="nowrap">
          <CancelButton
            onClick={() => {
              setFile(null)
              setImageSrc(null)
              onClose && onClose()
            }}
          />
          <DeleteButton
            disabled={!file && !initialImageUrl}
            variant="outline"
            color="red"
            onClick={async () => {
              if (file) {
                setFile(null)
                setImageSrc(null)
              } else if (onDelete) {
                try {
                  setLoading(true)
                  await onDelete()
                  onClose && onClose()
                } finally {
                  setLoading(false)
                }
              }
            }}
          />
          <Button
            children={t("Upload")}
            disabled={!file}
            onClick={async () => await handleConfirmUpload()}
          />
        </FormButtonGroup>
        <LoadingOverlay zIndex={1000} visible={loading} />
      </Stack>

      <ImageCropper
        opened={cropperOpened}
        imageSrc={imageSrc}
        rawFile={null}
        aspect={aspect}
        onConfirm={(croppedFile: File) => {
          setFile(croppedFile)
          setCropperOpened(false)
          if (imageSrc) URL.revokeObjectURL(imageSrc)
          setImageSrc(null)
        }}
        onCancel={() => {
          setCropperOpened(false)
          if (imageSrc) {
            URL.revokeObjectURL(imageSrc)
            setImageSrc(null)
          }
        }}
      />
    </DefaultModal>
  )
}

export default PickAndCropImageModal
